import { useEffect, useMemo, useRef, useState } from "react";
import { useResourceLimits } from "@/hooks/useResourceLimits";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/sonner";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  Instagram,
  Loader2,
  Megaphone,
  MessageSquare,
  Phone,
} from "lucide-react";
import { buildTriggerConfig, maxExecutionsForTrigger } from "@/lib/automation-flow";
import { mapIntegrationToCampaignOption } from "@/lib/campaign-integration-options";
import { formatSupabaseError, isMissingColumnError } from "@/lib/supabase-error";
import {
  CAMPAIGN_CHANNELS,
  CAMPAIGN_CHANNEL_LABELS,
  DEFAULT_TRIGGER_BY_CAMPAIGN_CHANNEL,
  TRAFFIC_SOURCES,
  integrationProvidersForCampaignChannel,
  parseKeywordList,
  type CampaignChannel,
  type CampaignIntegrationOption,
} from "@/lib/automation-campaign";

const CAMPAIGN_CHANNEL_ICONS: Record<CampaignChannel, typeof MessageSquare> = {
  whatsapp: MessageSquare,
  instagram_dm: Instagram,
  instagram_story: Instagram,
  instagram_comment: Instagram,
};

const CAMPAIGN_CHANNEL_COLORS: Record<CampaignChannel, string> = {
  whatsapp: "text-green-500",
  instagram_dm: "text-pink-500",
  instagram_story: "text-purple-500",
  instagram_comment: "text-orange-500",
};

interface CampaignWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: string;
  onCreated: () => void;
}

export function CampaignWizard({ open, onOpenChange, workspaceId, onCreated }: CampaignWizardProps) {
  const { checkCanCreate } = useResourceLimits(workspaceId);
  const submittingRef = useRef(false);
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState("");
  const [channel, setChannel] = useState<CampaignChannel>("whatsapp");
  const [integrationId, setIntegrationId] = useState("");
  const [integrations, setIntegrations] = useState<CampaignIntegrationOption[]>([]);
  const [loadingIntegrations, setLoadingIntegrations] = useState(false);
  const [leadMessage, setLeadMessage] = useState("");
  const [autoResponseEnabled, setAutoResponseEnabled] = useState(false);
  const [autoResponseMessage, setAutoResponseMessage] = useState("");
  const [trafficSource, setTrafficSource] = useState("");
  const [utmCampaign, setUtmCampaign] = useState("");
  const [adSetName, setAdSetName] = useState("");
  const [adName, setAdName] = useState("");
  const [notes, setNotes] = useState("");

  const parsedKeywords = useMemo(() => parseKeywordList(leadMessage), [leadMessage]);
  const selectedTraffic = TRAFFIC_SOURCES.find((s) => s.value === trafficSource);
  const selectedIntegration = integrations.find((i) => i.id === integrationId);

  useEffect(() => {
    if (!open || !workspaceId) return;

    let cancelled = false;
    (async () => {
      setLoadingIntegrations(true);
      try {
        const providers = integrationProvidersForCampaignChannel(channel);
        const { data, error } = await supabase
          .from("integrations")
          .select("id, provider, config, instance_identifier")
          .eq("workspace_id", workspaceId)
          .in("provider", providers as unknown as ("instagram" | "messenger" | "netface" | "shopify" | "whatsapp_cloud" | "whatsapp_evolution" | "whatsapp_uazapi")[])
          .eq("status", "active")
          .order("created_at", { ascending: true });

        if (cancelled) return;
        if (error) throw error;

        const opts = (data || []).map((row) =>
          mapIntegrationToCampaignOption(row as Parameters<typeof mapIntegrationToCampaignOption>[0]),
        );
        setIntegrations(opts);
        setIntegrationId((prev) => {
          if (prev && opts.some((o) => o.id === prev)) return prev;
          return opts.length === 1 ? opts[0].id : "";
        });
      } catch (err) {
        console.error("Campaign integrations load error:", err);
        setIntegrations([]);
        setIntegrationId("");
      } finally {
        if (!cancelled) setLoadingIntegrations(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, workspaceId, channel]);

  const handleChannelChange = (next: CampaignChannel) => {
    setChannel(next);
    setIntegrationId("");
  };

  const resetForm = () => {
    setStep(0);
    setName("");
    setChannel("whatsapp");
    setIntegrationId("");
    setIntegrations([]);
    setLeadMessage("");
    setAutoResponseEnabled(false);
    setAutoResponseMessage("");
    setTrafficSource("");
    setUtmCampaign("");
    setAdSetName("");
    setAdName("");
    setNotes("");
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const canAdvance = () => {
    switch (step) {
      case 0:
        return (
          name.trim().length > 0 &&
          channel.length > 0 &&
          trafficSource.length > 0 &&
          integrationId.length > 0
        );
      case 1:
        return (
          parsedKeywords.length > 0 &&
          (!autoResponseEnabled || autoResponseMessage.trim().length > 0)
        );
      case 2:
        return true;
      default:
        return true;
    }
  };

  const handleSave = async () => {
    if (submittingRef.current || saving) return;
    submittingRef.current = true;
    setSaving(true);
    let automationId: string | null = null;

    try {
      const limitCheck = await checkCanCreate("automations");
      if (!limitCheck.can_create) {
        toast.error(`Limite de automações atingido (${limitCheck.current}/${limitCheck.limit})`);
        return;
      }

      if (!integrationId) {
        toast.error("Selecione o número ou conta que receberá esta campanha.");
        return;
      }

      const triggerType = DEFAULT_TRIGGER_BY_CAMPAIGN_CHANNEL[channel];
      const triggerConfig = buildTriggerConfig(triggerType, {
        keywords: parsedKeywords,
        integration_id: integrationId,
      });

      const aiControlMode = autoResponseEnabled ? "continuous" : "fallback_ai";

      const { data: automation, error: automationError } = await supabase
        .from("automations")
        .insert({
          workspace_id: workspaceId,
          name: `[Campanha] ${name.trim()}`,
          channel,
          status: "draft",
          trigger_type: triggerType,
          trigger_config: triggerConfig,
          ai_control_mode: aiControlMode,
          max_executions_per_contact: maxExecutionsForTrigger(triggerType),
          cooldown_seconds: 0,
        } as any)
        .select("id")
        .single();

      if (automationError) throw automationError;

      automationId = (automation as { id: string }).id;

      const nodeDefs: Array<{ node_type: string; config: Record<string, unknown>; label: string }> = [
        {
          node_type: "trigger",
          config: { trigger_type: triggerType, ...triggerConfig },
          label: "Gatilho",
        },
      ];

      if (autoResponseEnabled) {
        nodeDefs.push({
          node_type: "send_message",
          config: { message: autoResponseMessage.trim() },
          label: "Resposta automática",
        });
      }

      nodeDefs.push({ node_type: "end", config: {}, label: "Fim" });

      const baseY = 50;
      const stepY = 150;
      const nodesPayload = nodeDefs.map((n, i) => ({
        automation_id: automationId,
        workspace_id: workspaceId,
        node_type: n.node_type,
        position_x: 250,
        position_y: baseY + i * stepY,
        config: n.config,
        label: n.label,
      }));

      const { data: nodes, error: nodesError } = await supabase
        .from("automation_nodes")
        .insert(nodesPayload as any)
        .select("id");

      if (nodesError) throw nodesError;

      const nodeIds = (nodes as { id: string }[]).map((n) => n.id);

      if (nodeIds.length >= 2) {
        const edgesPayload = [];
        for (let i = 0; i < nodeIds.length - 1; i++) {
          edgesPayload.push({
            automation_id: automationId,
            workspace_id: workspaceId,
            source_node_id: nodeIds[i],
            target_node_id: nodeIds[i + 1],
            branch: "default",
          });
        }
        const { error: edgesError } = await supabase.from("automation_edges").insert(edgesPayload as any);
        if (edgesError) throw edgesError;
      }

      const compiledNodes = nodeDefs.map((n, i) => ({
        id: nodeIds[i],
        type: n.node_type,
        config: n.config,
        label: n.label,
      }));
      const compiledEdges: Array<{ source: string; target: string; branch: string }> = [];
      for (let i = 0; i < nodeIds.length - 1; i++) {
        compiledEdges.push({
          source: nodeIds[i],
          target: nodeIds[i + 1],
          branch: "default",
        });
      }

      const { error: compileError } = await supabase
        .from("automations")
        .update({ compiled_flow: { nodes: compiledNodes, edges: compiledEdges } } as any)
        .eq("id", automationId);

      if (compileError) throw compileError;

      const campaignPayload: Record<string, unknown> = {
        workspace_id: workspaceId,
        automation_id: automationId,
        name: name.trim(),
        channel,
        keywords: parsedKeywords,
        status: "draft",
        utm_source: trafficSource || null,
        utm_medium: selectedTraffic?.utmMedium || null,
        utm_campaign: utmCampaign.trim() || null,
        ad_set_name: adSetName.trim() || null,
        ad_name: adName.trim() || null,
        notes: notes.trim() || null,
        integration_id: integrationId,
      };

      let { error: campaignError } = await (supabase as any)
        .from("automation_campaigns")
        .insert(campaignPayload);

      if (campaignError && isMissingColumnError(campaignError, "integration_id")) {
        const { integration_id: _ignored, ...payloadWithoutIntegration } = campaignPayload;
        ({ error: campaignError } = await (supabase as any)
          .from("automation_campaigns")
          .insert(payloadWithoutIntegration));
      }

      if (campaignError) throw campaignError;

      toast.success("Campanha criada como rascunho. Ative na aba Campanhas para começar a rastrear.");
      handleClose();
      onCreated();
    } catch (err: unknown) {
      console.error("Campaign create error:", err);
      if (automationId) {
        await supabase.from("automations").delete().eq("id", automationId);
      }
      toast.error(formatSupabaseError(err, "Erro ao criar campanha"));
    } finally {
      setSaving(false);
      submittingRef.current = false;
    }
  };

  const handleOpenChange = (nextOpen: boolean) => {
    if (!nextOpen) handleClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Megaphone className="h-5 w-5 text-primary" />
            Nova campanha
          </DialogTitle>
          <div className="flex items-center gap-1.5 pt-2">
            {[0, 1, 2, 3].map((i) => (
              <div
                key={i}
                className={`h-1.5 flex-1 rounded-full transition-colors ${
                  i <= step ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {step === 0 && (
            <>
              <div className="space-y-2">
                <Label htmlFor="campaign-name">Nome da campanha</Label>
                <Input
                  id="campaign-name"
                  placeholder="Black Friday WhatsApp"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label>Canal de atendimento</Label>
                <p className="text-xs text-muted-foreground">
                  Onde o lead manda a mensagem do anúncio (WhatsApp, Instagram, etc.).
                </p>
                <div className="grid grid-cols-2 gap-2">
                  {CAMPAIGN_CHANNELS.map((ch) => {
                    const Icon = CAMPAIGN_CHANNEL_ICONS[ch];
                    return (
                      <button
                        key={ch}
                        type="button"
                        onClick={() => handleChannelChange(ch)}
                        className={`flex items-center gap-2 p-3 rounded-lg border text-left text-sm transition-colors ${
                          channel === ch
                            ? "border-primary bg-primary/5"
                            : "border-border hover:border-primary/50"
                        }`}
                      >
                        <Icon className={`h-4 w-4 shrink-0 ${CAMPAIGN_CHANNEL_COLORS[ch]}`} />
                        {CAMPAIGN_CHANNEL_LABELS[ch]}
                      </button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>
                  {channel === "whatsapp" ? "Número WhatsApp" : "Conta Instagram"}
                </Label>
                <p className="text-xs text-muted-foreground">
                  Qual {channel === "whatsapp" ? "número" : "conta"} receberá as mensagens desta campanha.
                  Você pode criar campanhas diferentes para cada {channel === "whatsapp" ? "número" : "conta"}.
                </p>
                {loadingIntegrations ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground py-3">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Carregando conexões...
                  </div>
                ) : integrations.length === 0 ? (
                  <div className="rounded-lg border border-dashed p-4 text-sm text-muted-foreground">
                    Nenhuma conexão ativa para {CAMPAIGN_CHANNEL_LABELS[channel]}.
                    Conecte em Integrações antes de criar a campanha.
                  </div>
                ) : (
                  <div className="space-y-1 max-h-48 overflow-y-auto">
                    {integrations.map((integ) => {
                      const isSelected = integrationId === integ.id;
                      return (
                        <button
                          key={integ.id}
                          type="button"
                          onClick={() => setIntegrationId(integ.id)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                            isSelected
                              ? "border-primary bg-primary/5"
                              : "border-border hover:border-primary/50"
                          }`}
                        >
                          <div
                            className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                              isSelected ? "bg-primary text-primary-foreground" : "bg-muted"
                            }`}
                          >
                            {channel === "whatsapp" ? (
                              <Phone className="h-4 w-4" />
                            ) : (
                              <Instagram className="h-4 w-4" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-medium truncate">{integ.display_name}</p>
                            {integ.detail && (
                              <p className="text-xs text-muted-foreground truncate">{integ.detail}</p>
                            )}
                          </div>
                          {isSelected && <Check className="h-4 w-4 text-primary shrink-0 ml-auto" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Canal de origem do tráfego</Label>
                <p className="text-xs text-muted-foreground">
                  De onde vem esta campanha (Google Ads, Instagram Ads, site, etc.).
                </p>
                <Select value={trafficSource} onValueChange={setTrafficSource}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o canal de origem..." />
                  </SelectTrigger>
                  <SelectContent>
                    {TRAFFIC_SOURCES.map((src) => (
                      <SelectItem key={src.value} value={src.value}>
                        {src.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {step === 1 && (
            <>
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline">{CAMPAIGN_CHANNEL_LABELS[channel]}</Badge>
                {selectedIntegration && (
                  <Badge variant="outline">
                    {channel === "whatsapp" ? "Número" : "Conta"}: {selectedIntegration.display_name}
                  </Badge>
                )}
                {selectedTraffic && (
                  <Badge variant="secondary">{selectedTraffic.label}</Badge>
                )}
              </div>

              <div className="rounded-lg border bg-muted/30 p-3 text-xs text-muted-foreground space-y-1">
                <p className="font-medium text-foreground">Como funciona</p>
                <p>
                  A campanha <strong>rastreia</strong> quando alguém manda a mensagem do seu anúncio.
                  Quem responde depois é opcional: o agente de IA do workspace (se ativo) ou uma resposta automática que você configurar abaixo.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="lead-message">Mensagem que o lead envia (CTA do anúncio)</Label>
                <Input
                  id="lead-message"
                  placeholder="QUERO PROMO"
                  value={leadMessage}
                  onChange={(e) => setLeadMessage(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Texto que você pede no anúncio para o lead mandar no canal. Use vírgula só se tiver mais de uma variação (ex.: QUERO PROMO, quero promoção).
                </p>
                {parsedKeywords.length > 0 && (
                  <div className="flex flex-wrap gap-1">
                    {parsedKeywords.map((kw) => (
                      <Badge key={kw} variant="secondary" className="text-xs">
                        {kw}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>

              <div className="rounded-lg border p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="auto-response" className="text-sm font-medium cursor-pointer">
                      Enviar resposta automática
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Opcional. Se desligado, o agente de IA responde normalmente (quando ativo no workspace).
                      Se ligado, a IA só assume depois que o lead responder à sua mensagem automática.
                    </p>
                  </div>
                  <Switch
                    id="auto-response"
                    checked={autoResponseEnabled}
                    onCheckedChange={setAutoResponseEnabled}
                  />
                </div>

                {autoResponseEnabled && (
                  <div className="space-y-2 pt-2 border-t">
                    <Label htmlFor="auto-response-message">Texto da resposta automática</Label>
                    <Textarea
                      id="auto-response-message"
                      placeholder="Olá! Obrigado pelo interesse. Temos condições especiais hoje — me conta o que você procura?"
                      value={autoResponseMessage}
                      onChange={(e) => setAutoResponseMessage(e.target.value)}
                      rows={4}
                    />
                  </div>
                )}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="space-y-2">
                <Label className="text-xs">Nome da campanha (ads)</Label>
                <Input
                  value={utmCampaign}
                  onChange={(e) => setUtmCampaign(e.target.value)}
                  placeholder="black-friday-2026"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-2">
                  <Label className="text-xs">Conjunto de anúncios</Label>
                  <Input
                    value={adSetName}
                    onChange={(e) => setAdSetName(e.target.value)}
                    placeholder="Público quente"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs">Nome do anúncio</Label>
                  <Input
                    value={adName}
                    onChange={(e) => setAdName(e.target.value)}
                    placeholder="Criativo vídeo A"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label className="text-xs">Observações</Label>
                <Textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Link do criativo, público-alvo..."
                  rows={2}
                />
              </div>
            </>
          )}

          {step === 3 && (
            <div className="space-y-3 text-sm">
              <div className="rounded-lg border p-4 space-y-2">
                <p><span className="text-muted-foreground">Campanha:</span> {name}</p>
                <p><span className="text-muted-foreground">Canal de atendimento:</span> {CAMPAIGN_CHANNEL_LABELS[channel]}</p>
                <p>
                  <span className="text-muted-foreground">
                    {channel === "whatsapp" ? "Número" : "Conta"}:
                  </span>{" "}
                  {selectedIntegration?.display_name || "—"}
                  {selectedIntegration?.detail ? ` (${selectedIntegration.detail})` : ""}
                </p>
                <p><span className="text-muted-foreground">Canal de origem:</span> {selectedTraffic?.label || trafficSource}</p>
                <p><span className="text-muted-foreground">Rastreio:</span> {parsedKeywords.join(", ")}</p>
                <p>
                  <span className="text-muted-foreground">Resposta:</span>{" "}
                  {autoResponseEnabled
                    ? `Automática — "${autoResponseMessage.trim().slice(0, 60)}${autoResponseMessage.trim().length > 60 ? "…" : ""}"`
                    : "Agente de IA (se ativo no workspace)"}
                </p>
                {utmCampaign && (
                  <p><span className="text-muted-foreground">UTM Campaign:</span> {utmCampaign}</p>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Uma automação vinculada será criada em rascunho. Ative-a para começar a contabilizar métricas.
              </p>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          {step > 0 ? (
            <Button type="button" variant="outline" onClick={() => setStep((s) => s - 1)} disabled={saving}>
              <ArrowLeft className="h-4 w-4 mr-1" />
              Voltar
            </Button>
          ) : (
            <Button type="button" variant="outline" onClick={handleClose} disabled={saving}>
              Cancelar
            </Button>
          )}

          {step < 3 ? (
            <Button type="button" onClick={() => setStep((s) => s + 1)} disabled={!canAdvance()}>
              Próximo
              <ArrowRight className="h-4 w-4 ml-1" />
            </Button>
          ) : (
            <Button type="button" onClick={handleSave} disabled={saving}>
              {saving ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <Check className="h-4 w-4 mr-1" />
                  Criar campanha
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
